import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useId, useState, type JSX, type ReactNode } from 'react';

import { InvitationResultDialog } from '@/components/InvitationResultDialog';
import { Modal } from '@/components/Modal';
import { Select, type SelectOption } from '@/components/ui/Select';
import { ApiError } from '@/lib/api';
import { checkNit, sanitizeSubdomain } from '@/lib/tenant-identity';

import styles from './CreateTenantWizard.module.scss';
import { ModuleSelectionField } from './ModuleSelectionField';
import {
  createTenant,
  type CreateTenantInput,
  type CreateTenantResult,
} from '../api/tenants.graphql';
import {
  selectionCheckState,
  useModuleCatalog,
  useModulePresets,
  useModuleSelectionCheck,
} from '../hooks/useModuleCatalog';
import { subdomainQueryKey, useSubdomainAvailability } from '../hooks/useSubdomainAvailability';
import { moduleNames } from '../module-labels';
import { COUNTRY_OPTIONS, PLAN_TIER_OPTIONS } from '../select-options';
import { bogotaDateInput, PILOT_END_RANGE } from '../tenant-labels';

interface Props {
  onClose: () => void;
  onCreated: () => void;
}

type Step = 1 | 2 | 3 | 4 | 5 | 6;

const DOCUMENT_TYPE_OPTIONS: ReadonlyArray<SelectOption> = [
  { value: 'CC', label: 'CC' },
  { value: 'CE', label: 'CE' },
  { value: 'PASAPORTE', label: 'Pasaporte' },
  { value: 'PPT', label: 'PPT' },
];

const LAST_STEP: Step = 6;

const STEPS: { step: Step; label: string }[] = [
  { step: 1, label: 'Empresa' },
  { step: 2, label: 'Paquete y piloto' },
  { step: 3, label: 'Módulos' },
  { step: 4, label: 'Administrador' },
  { step: 5, label: 'Primera sede' },
  { step: 6, label: 'Confirmar' },
];

interface WizardForm {
  name: string;
  legalName: string;
  email: string;
  nit: string;
  subdomain: string;
  planTier: string;
  country: string;
  /** OS-1720 — los clientes nuevos son pilotos (D4a); "pago" crea el tenant ya activo. */
  clientType: 'pilot' | 'paid';
  /** OS-1720 — `YYYY-MM-DD`, obligatorio para un piloto; sin duración por defecto (D1a). */
  pilotEndsAt: string;
  adminEmail: string;
  adminCedula: string;
  adminDocumentType: string;
  locationName: string;
  locationAddress: string;
  locationCity: string;
  locationCode: string;
  /**
   * OS-1716 — módulos elegidos. `null` hasta que el operador elige un preset o marca módulos: el
   * paso no deja avanzar sin una elección explícita (solo se muestran marcados los módulos base).
   */
  modules: string[] | null;
}

type FieldErrors = Partial<Record<keyof WizardForm, string>>;
type TextField = Exclude<keyof WizardForm, 'modules'>;

const INITIAL: WizardForm = {
  name: '',
  legalName: '',
  email: '',
  nit: '',
  subdomain: '',
  planTier: 'trial',
  country: 'CO',
  clientType: 'pilot',
  pilotEndsAt: '',
  adminEmail: '',
  adminCedula: '',
  adminDocumentType: 'CC',
  locationName: 'Sede Principal',
  locationAddress: '',
  locationCity: '',
  locationCode: '',
  modules: null,
};

/** Borrador por pestaña. Nunca incluye la contraseña. */
export const WIZARD_DRAFT_KEY = 'opssynk-admin:create-tenant-draft';

const STEP_OF_FIELD: Record<keyof WizardForm, Step> = {
  name: 1,
  legalName: 1,
  email: 1,
  nit: 1,
  subdomain: 2,
  planTier: 2,
  country: 2,
  clientType: 2,
  pilotEndsAt: 2,
  modules: 3,
  adminEmail: 4,
  adminCedula: 4,
  adminDocumentType: 4,
  locationName: 5,
  locationAddress: 5,
  locationCity: 5,
  locationCode: 5,
};

/** Campo del API (`field` del error) → campo del formulario. */
const FORM_FIELD_OF_API_FIELD: Record<string, keyof WizardForm> = {
  name: 'name',
  nit: 'nit',
  verificationDigit: 'nit',
  subdomain: 'subdomain',
  pilotEndsAt: 'pilotEndsAt',
  'admin.email': 'adminEmail',
  'admin.cedula': 'adminCedula',
  'admin.documentType': 'adminDocumentType',
  'location.name': 'locationName',
  'location.address': 'locationAddress',
  modules: 'modules',
};

const MODULES_REQUIRED_MESSAGE =
  'Elige un preset o marca los módulos contratados antes de continuar.';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
function readDraft(): { form: WizardForm; step: Step } | null {
  try {
    const raw = sessionStorage.getItem(WIZARD_DRAFT_KEY);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw) as { form?: Partial<WizardForm>; step?: number };
    const step =
      parsed.step && parsed.step >= 1 && parsed.step <= LAST_STEP ? (parsed.step as Step) : 1;
    const modules = parsed.form?.modules;
    return {
      form: {
        ...INITIAL,
        ...parsed.form,
        clientType: parsed.form?.clientType === 'paid' ? 'paid' : 'pilot',
        modules:
          Array.isArray(modules) && modules.every((key) => typeof key === 'string') ? modules : null,
      },
      step,
    };
  } catch {
    return null;
  }
}

function writeDraft(form: WizardForm, step: Step): void {
  try {
    sessionStorage.setItem(WIZARD_DRAFT_KEY, JSON.stringify({ form, step }));
  } catch {
    // Sin storage (modo privado): el wizard funciona igual, sin borrador.
  }
}

export function clearWizardDraft(): void {
  try {
    sessionStorage.removeItem(WIZARD_DRAFT_KEY);
  } catch {
    // Nada que limpiar.
  }
}

function hasUserData(form: WizardForm): boolean {
  return (Object.keys(INITIAL) as (keyof WizardForm)[]).some(
    (key) => JSON.stringify(form[key]) !== JSON.stringify(INITIAL[key]),
  );
}

export function CreateTenantWizard({ onClose, onCreated }: Props): JSX.Element {
  const [initialDraft] = useState(readDraft);
  const [step, setStep] = useState<Step>(initialDraft?.step ?? 1);
  const [form, setForm] = useState<WizardForm>(initialDraft?.form ?? INITIAL);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [focusField, setFocusField] = useState<keyof WizardForm | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [confirmingClose, setConfirmingClose] = useState(false);
  const [created, setCreated] = useState<CreateTenantResult | null>(null);
  const idPrefix = useId();
  const queryClient = useQueryClient();
  const subdomainStatus = useSubdomainAvailability(form.subdomain);
  const catalog = useModuleCatalog();
  const presets = useModulePresets();
  const selectedModules = form.modules ?? [];
  const moduleCheckQuery = useModuleSelectionCheck(
    selectedModules,
    step === 3 && Boolean(catalog.data),
  );
  const moduleCheck = selectionCheckState(moduleCheckQuery);

  const fieldId = (key: keyof WizardForm): string => `${idPrefix}-${key}`;
  const errorId = (key: keyof WizardForm): string => `${idPrefix}-${key}-error`;
  // OS-1720 — mismos límites técnicos que el API (mañana a 365 días, calendario de Colombia).
  const pilotMin = bogotaDateInput(PILOT_END_RANGE.minDays);
  const pilotMax = bogotaDateInput(PILOT_END_RANGE.maxDays);

  useEffect(() => {
    if (!created) {
      writeDraft(form, step);
    }
  }, [form, step, created]);

  useEffect(() => {
    if (focusField) {
      document.getElementById(`${idPrefix}-${focusField}`)?.focus();
      setFocusField(null);
    }
  }, [focusField, step, idPrefix]);

  const mutation = useMutation({
    mutationFn: createTenant,
    onSuccess: (result) => {
      clearWizardDraft();
      setCreated(result);
    },
    onError: (error) => {
      const apiField = error instanceof ApiError ? error.field : undefined;
      const key = apiField ? FORM_FIELD_OF_API_FIELD[apiField] : undefined;
      if (!key) {
        setSubmitError(error.message);
        return;
      }
      if (key === 'subdomain') {
        void queryClient.invalidateQueries({ queryKey: subdomainQueryKey(form.subdomain) });
      }
      setSubmitError(null);
      setErrors({ [key]: error.message });
      setStep(STEP_OF_FIELD[key]);
      setFocusField(key);
    },
  });

  function updateForm<K extends keyof WizardForm>(key: K, value: WizardForm[K]): void {
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => {
      if (!(key in prev)) {
        return prev;
      }
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }

  function validate(current: Step): FieldErrors {
    const found: FieldErrors = {};
    if (current === 1) {
      if (!form.name.trim()) {
        found.name = 'El nombre comercial es obligatorio.';
      }
      const nit = form.nit.trim() ? checkNit(form.nit) : null;
      if (nit && !nit.ok) {
        found.nit = nit.message;
      }
      if (form.email.trim() && !EMAIL_PATTERN.test(form.email.trim())) {
        found.email = 'Escribe un correo válido, p. ej. contacto@empresa.com.';
      }
    }
    if (current === 2) {
      if (subdomainStatus.state === 'unavailable' || subdomainStatus.state === 'error') {
        found.subdomain = subdomainStatus.message;
      } else if (subdomainStatus.state === 'checking') {
        found.subdomain = 'Espera a que termine la verificación del subdominio.';
      }
      if (!/^[A-Za-z]{2}$/.test(form.country.trim())) {
        found.country = 'Selecciona el país del tenant. No hay valor por defecto.';
      }
      if (form.clientType === 'pilot') {
        if (!form.pilotEndsAt) {
          found.pilotEndsAt = 'Indica la fecha de fin del piloto.';
        } else if (form.pilotEndsAt < pilotMin || form.pilotEndsAt > pilotMax) {
          found.pilotEndsAt = `La fecha debe estar entre ${pilotMin} y ${pilotMax}.`;
        }
      }
    }
    if (current === 3) {
      if (!catalog.data) {
        found.modules = 'Espera a que cargue el catálogo de módulos.';
      } else if (form.modules === null) {
        found.modules = MODULES_REQUIRED_MESSAGE;
      } else if (moduleCheck.state === 'checking') {
        found.modules = 'Espera a que termine la validación de los módulos.';
      } else if (moduleCheck.state !== 'valid') {
        found.modules = moduleCheck.message;
      }
    }
    if (current === 4) {
      if (!EMAIL_PATTERN.test(form.adminEmail.trim())) {
        found.adminEmail = 'Escribe el correo del administrador.';
      }
      if (!form.adminCedula.trim()) {
        found.adminCedula = 'La cédula es obligatoria.';
      }
    }
    if (current === 5) {
      if (!form.locationName.trim()) {
        found.locationName = 'El nombre de la sede es obligatorio.';
      }
      if (!form.locationAddress.trim()) {
        found.locationAddress = 'La dirección es obligatoria.';
      }
    }
    return found;
  }

  function goNext(): void {
    const found = validate(step);
    setErrors(found);
    const first = Object.keys(found)[0] as keyof WizardForm | undefined;
    if (first) {
      setFocusField(first);
      return;
    }
    setStep((s) => (s + 1) as Step);
  }

  function submit(): void {
    setSubmitError(null);
    // Un borrador anterior al paso de módulos puede llegar aquí sin elección: se vuelve al paso 3.
    if (form.modules === null) {
      setErrors({ modules: MODULES_REQUIRED_MESSAGE });
      setStep(3);
      setFocusField('modules');
      return;
    }
    const nit = form.nit.trim() ? checkNit(form.nit) : null;
    const input: CreateTenantInput = {
      name: form.name.trim(),
      ...(form.legalName.trim() && { legalName: form.legalName.trim() }),
      ...(form.email.trim() && { email: form.email.trim() }),
      ...(nit?.ok && { nit: nit.nit, verificationDigit: nit.verificationDigit }),
      ...(form.subdomain && { subdomain: form.subdomain }),
      planTier: form.planTier,
      country: form.country,
      ...(form.clientType === 'pilot' && { pilotEndsAt: form.pilotEndsAt }),
      // Siempre explícito: el paso 3 exige elegir. (El API conserva "sin modules = todos" para
      // otros clientes; la consola ya no depende de ese default.)
      modules: form.modules ?? [],
      admin: {
        email: form.adminEmail.trim(),
        cedula: form.adminCedula.trim(),
        documentType: form.adminDocumentType,
      },
      location: {
        name: form.locationName.trim(),
        address: form.locationAddress.trim(),
        ...(form.locationCity.trim() && { city: form.locationCity.trim() }),
        ...(form.locationCode.trim() && { code: form.locationCode.trim() }),
      },
    };
    mutation.mutate(input);
  }

  function requestClose(): void {
    if (hasUserData(form)) {
      setConfirmingClose(true);
      return;
    }
    clearWizardDraft();
    onClose();
  }

  function discardAndClose(): void {
    clearWizardDraft();
    onClose();
  }

  if (created) {
    return (
      <InvitationResultDialog
        email={created.adminUser.email}
        delivery={created.adminInvitation.emailDelivery}
        link={created.adminInvitation.activationUrl}
        expiresAt={created.adminInvitation.expiresAt}
        resendAction="Reenviar invitación"
        purpose="definir su contraseña y activar la cuenta"
        onClose={onCreated}
      />
    );
  }

  if (confirmingClose) {
    return (
      <Modal
        title="¿Cerrar el alta del tenant?"
        onRequestClose={() => setConfirmingClose(false)}
        closeLabel="Seguir editando"
        footer={
          <>
            <button className={styles.backBtn} type="button" onClick={discardAndClose}>
              Descartar borrador
            </button>
            <button className={styles.backBtn} type="button" onClick={onClose}>
              Cerrar y conservar borrador
            </button>
            <button
              className={styles.confirmBtn}
              type="button"
              data-autofocus
              onClick={() => setConfirmingClose(false)}
            >
              Seguir editando
            </button>
          </>
        }
      >
        <p className={styles.hint}>
          Tienes datos sin guardar. El borrador se conserva en esta pestaña del navegador (sin la
          contraseña) y se recupera al volver a abrir el asistente.
        </p>
      </Modal>
    );
  }

  const fieldProps = (
    key: TextField,
  ): {
    id: string;
    value: string;
    'aria-invalid': boolean;
    'aria-describedby': string | undefined;
  } => ({
    id: fieldId(key),
    value: form[key],
    'aria-invalid': Boolean(errors[key]),
    'aria-describedby': errors[key] ? errorId(key) : undefined,
  });

  const fieldError = (key: keyof WizardForm): ReactNode =>
    errors[key] ? (
      <span id={errorId(key)} className={styles.fieldError} role="alert">
        {errors[key]}
      </span>
    ) : null;

  const nitPreview = form.nit.trim() && !errors.nit ? checkNit(form.nit) : null;

  return (
    <Modal
      title="Nuevo tenant"
      onRequestClose={mutation.isPending ? undefined : requestClose}
      closeLabel="Cerrar asistente"
      headerExtra={
        <ol className={styles.steps} aria-label={`Paso ${step} de ${STEPS.length}`}>
          {STEPS.map((s) => (
            <li
              key={s.step}
              aria-current={step === s.step ? 'step' : undefined}
              title={s.label}
              className={`${styles.step} ${step === s.step ? styles.stepActive : step > s.step ? styles.stepDone : ''}`}
            >
              {s.step}
            </li>
          ))}
        </ol>
      }
      footer={
        <>
          {step > 1 && (
            <button
              className={styles.backBtn}
              type="button"
              disabled={mutation.isPending}
              onClick={() => {
                setErrors({});
                setStep((s) => (s - 1) as Step);
              }}
            >
              Atrás
            </button>
          )}
          {step < LAST_STEP && (
            <button className={styles.nextBtn} type="button" onClick={goNext}>
              Siguiente
            </button>
          )}
          {step === LAST_STEP && (
            <button
              className={styles.confirmBtn}
              type="button"
              onClick={submit}
              disabled={mutation.isPending}
            >
              {mutation.isPending ? 'Creando…' : 'Crear tenant'}
            </button>
          )}
        </>
      }
    >
      {step === 1 && (
        <div className={styles.stepContent}>
          <h3 className={styles.stepTitle}>Datos de la empresa</h3>
          <div className={styles.fields}>
            <label>
              Nombre comercial *
              <input
                type="text"
                {...fieldProps('name')}
                onChange={(e) => updateForm('name', e.target.value)}
                placeholder="Mi Restaurante"
              />
              {fieldError('name')}
            </label>
            <label>
              Razón social
              <input
                type="text"
                {...fieldProps('legalName')}
                onChange={(e) => updateForm('legalName', e.target.value)}
                placeholder="Mi Restaurante S.A.S."
              />
            </label>
            <label>
              NIT (con dígito de verificación)
              <input
                type="text"
                inputMode="numeric"
                {...fieldProps('nit')}
                onChange={(e) => updateForm('nit', e.target.value)}
                onBlur={() => {
                  const result = form.nit.trim() ? checkNit(form.nit) : null;
                  if (result && !result.ok) {
                    setErrors((prev) => ({ ...prev, nit: result.message }));
                  }
                }}
                placeholder="900123456-8"
              />
              {fieldError('nit')}
              {nitPreview?.ok ? (
                <span className={styles.fieldOk}>NIT válido.</span>
              ) : null}
            </label>
            <label>
              Email de contacto
              <input
                type="email"
                {...fieldProps('email')}
                onChange={(e) => updateForm('email', e.target.value)}
                placeholder="contacto@empresa.com"
              />
              {fieldError('email')}
            </label>
          </div>
        </div>
      )}

      {step === 2 && (
        <div className={styles.stepContent}>
          <h3 className={styles.stepTitle}>Subdominio, paquete y piloto</h3>
          <div className={styles.fields}>
            <label>
              Subdominio (opcional)
              <div className={styles.subdomainInput}>
                <input
                  type="text"
                  {...fieldProps('subdomain')}
                  aria-describedby={`${idPrefix}-subdomain-status`}
                  onChange={(e) => updateForm('subdomain', sanitizeSubdomain(e.target.value))}
                  placeholder="mi-restaurante"
                />
                <span className={styles.subdomainSuffix}>.opssynk.com</span>
              </div>
              <span id={`${idPrefix}-subdomain-status`} aria-live="polite">
                {errors.subdomain ? (
                  <span className={styles.fieldError} role="alert">
                    {errors.subdomain}
                  </span>
                ) : subdomainStatus.state === 'checking' ? (
                  <span className={styles.fieldHint}>Verificando disponibilidad…</span>
                ) : subdomainStatus.state === 'available' ? (
                  <span className={styles.fieldOk}>Subdominio disponible.</span>
                ) : subdomainStatus.state === 'unavailable' || subdomainStatus.state === 'error' ? (
                  <span className={styles.fieldError}>{subdomainStatus.message}</span>
                ) : null}
              </span>
            </label>
            <label>
              Paquete comercial (informativo)
              <Select
                {...fieldProps('planTier')}
                onChange={(value) => updateForm('planTier', value)}
                options={PLAN_TIER_OPTIONS}
              />
              <span className={styles.fieldHint}>
                No define los módulos: se eligen en el paso siguiente.
              </span>
            </label>
            <fieldset className={styles.choiceGroup}>
              <legend>Tipo de cliente</legend>
              <label className={styles.choice}>
                <input
                  type="radio"
                  name={`${idPrefix}-client-type`}
                  checked={form.clientType === 'pilot'}
                  onChange={() => updateForm('clientType', 'pilot')}
                />
                Piloto con fecha de fin
              </label>
              <label className={styles.choice}>
                <input
                  type="radio"
                  name={`${idPrefix}-client-type`}
                  checked={form.clientType === 'paid'}
                  onChange={() => updateForm('clientType', 'paid')}
                />
                Cliente de pago desde el alta
              </label>
            </fieldset>
            {form.clientType === 'pilot' ? (
              <label>
                Fin del piloto *
                <input
                  type="date"
                  min={pilotMin}
                  max={pilotMax}
                  {...fieldProps('pilotEndsAt')}
                  onChange={(e) => updateForm('pilotEndsAt', e.target.value)}
                />
                {errors.pilotEndsAt ? (
                  <span id={errorId('pilotEndsAt')} className={styles.fieldError} role="alert">
                    {errors.pilotEndsAt}
                  </span>
                ) : (
                  <span className={styles.fieldHint}>
                    Termina al final de ese día (hora Colombia). Al vencer, la consola alerta pero
                    no suspende al tenant.
                  </span>
                )}
              </label>
            ) : null}
            <label>
              País
              <Select
                {...fieldProps('country')}
                onChange={(value) => updateForm('country', value)}
                options={COUNTRY_OPTIONS}
              />
            </label>
          </div>
        </div>
      )}

      {step === 3 && (
        <div className={styles.stepContent}>
          <h3 className={styles.stepTitle}>Módulos</h3>
          <p className={styles.hint}>
            Elige un preset o marca los módulos que contrató el cliente; sin una elección no se puede
            continuar. Los módulos base siempre se incluyen y todo se puede cambiar después en la
            pestaña Módulos del tenant.
          </p>
          {catalog.isLoading ? (
            <p className={styles.fieldHint} role="status">Cargando el catálogo de módulos…</p>
          ) : catalog.isError ? (
            <p className={styles.error} role="alert">
              No se pudo cargar el catálogo de módulos.{' '}
              <button type="button" className={styles.inlineBtn} onClick={() => void catalog.refetch()}>
                Reintentar
              </button>
            </p>
          ) : (
            <ModuleSelectionField
              id={fieldId('modules')}
              catalog={catalog.data ?? []}
              presets={presets.data ?? []}
              value={selectedModules}
              check={moduleCheck}
              onChange={(next) => updateForm('modules', next)}
            />
          )}
          {errors.modules && moduleCheck.state !== 'invalid' && moduleCheck.state !== 'error'
            ? fieldError('modules')
            : null}
        </div>
      )}

      {step === 4 && (
        <div className={styles.stepContent}>
          <h3 className={styles.stepTitle}>Usuario SUPER_ADMIN</h3>
          <p className={styles.hint}>
            Primer usuario del tenant con acceso total. Recibe una invitación por email para
            activar su cuenta.
          </p>
          <div className={styles.fields}>
            <label>
              Email *
              <input
                type="email"
                {...fieldProps('adminEmail')}
                onChange={(e) => updateForm('adminEmail', e.target.value)}
                placeholder="admin@empresa.com"
              />
              {fieldError('adminEmail')}
            </label>
            <label>
              Cédula *
              <input
                type="text"
                {...fieldProps('adminCedula')}
                onChange={(e) => updateForm('adminCedula', e.target.value)}
                placeholder="1234567890"
              />
              {fieldError('adminCedula')}
            </label>
            <label>
              Tipo de documento
              <Select
                {...fieldProps('adminDocumentType')}
                onChange={(value) => updateForm('adminDocumentType', value)}
                options={DOCUMENT_TYPE_OPTIONS}
              />
              {fieldError('adminDocumentType')}
            </label>
            <p className={styles.fieldHint}>
              No se crea contraseña: el administrador recibe un email para definir la suya y activar
              la cuenta (link de un solo uso, vence en 72 h).
            </p>
          </div>
        </div>
      )}

      {step === 5 && (
        <div className={styles.stepContent}>
          <h3 className={styles.stepTitle}>Primera sede</h3>
          <p className={styles.hint}>
            Sin al menos una sede activa, el login en la app queda bloqueado.
          </p>
          <div className={styles.fields}>
            <label>
              Nombre *
              <input
                type="text"
                {...fieldProps('locationName')}
                onChange={(e) => updateForm('locationName', e.target.value)}
                placeholder="Sede Principal"
              />
              {fieldError('locationName')}
            </label>
            <label>
              Dirección *
              <input
                type="text"
                {...fieldProps('locationAddress')}
                onChange={(e) => updateForm('locationAddress', e.target.value)}
                placeholder="Calle 1 # 2-3"
              />
              {fieldError('locationAddress')}
            </label>
            <label>
              Ciudad
              <input
                type="text"
                {...fieldProps('locationCity')}
                onChange={(e) => updateForm('locationCity', e.target.value)}
                placeholder="Bogotá"
              />
            </label>
            <label>
              Código interno
              <input
                type="text"
                {...fieldProps('locationCode')}
                onChange={(e) => updateForm('locationCode', e.target.value)}
                placeholder="SEDE-01"
              />
            </label>
          </div>
        </div>
      )}

      {step === LAST_STEP && (
        <div className={styles.stepContent}>
          <h3 className={styles.stepTitle}>Confirmar creación</h3>
          <dl className={styles.summary}>
            <dt>Empresa</dt>
            <dd>{form.name}</dd>
            {form.legalName ? (
              <>
                <dt>Razón social</dt>
                <dd>{form.legalName}</dd>
              </>
            ) : null}
            {form.nit ? (
              <>
                <dt>NIT</dt>
                <dd>{form.nit}</dd>
              </>
            ) : null}
            <dt>Subdominio</dt>
            <dd>{form.subdomain ? `${form.subdomain}.opssynk.com` : '(sin subdominio)'}</dd>
            <dt>Paquete</dt>
            <dd>{form.planTier}</dd>
            <dt>Tipo de cliente</dt>
            <dd>
              {form.clientType === 'pilot'
                ? `Piloto hasta el ${form.pilotEndsAt}`
                : 'Cliente de pago'}
            </dd>
            <dt>Módulos</dt>
            <dd>
              {catalog.data
                ? moduleNames(
                    catalog.data
                      .filter((module) => module.isCore || selectedModules.includes(module.key))
                      .map((module) => module.key),
                    new Map(catalog.data.map((module) => [module.key, module.name])),
                  )
                : '—'}
            </dd>
            <dt>Admin</dt>
            <dd>
              {form.adminEmail} · SUPER_ADMIN · {form.adminCedula}
            </dd>
            <dt>Acceso</dt>
            <dd>Invitación por email para definir su contraseña (sin contraseña temporal)</dd>
            <dt>Sede</dt>
            <dd>
              {form.locationName} — {form.locationAddress}
            </dd>
          </dl>
          {submitError ? (
            <p className={styles.error} role="alert">
              {submitError}
            </p>
          ) : null}
        </div>
      )}
    </Modal>
  );
}
